import { useState } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { yellow } from '@mui/material/colors';

import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';

import DownloadIcon from '@mui/icons-material/Download';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

dayjs.extend(relativeTime);

enum NotificationType {
  INFO,
  WELCOME,
  NEW_REPORT,
}

interface INotification {
  id: string;
  type: NotificationType;
  datetime: Date;
  label: string;
}

function Notifications() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // for test purpose
  const notifications: INotification[] = [
    {
      id: 'unique-id',
      type: NotificationType.NEW_REPORT,
      datetime: new Date('2024-06-24T09:03:55Z'),
      label: 'A new report is available',
    },
    {
      id: 'unique-id-2',
      type: NotificationType.WELCOME,
      datetime: new Date('2024-05-23T09:03:55Z'),
      label: 'Welcome to your Seacube dashboard !',
    },
  ].sort((a, b) => b.datetime.valueOf() - a.datetime.valueOf());

  const handleOpenNotifications = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleNewReportDownloadClick = (notification: INotification) => {
    // console.log(notification);
  };

  const tooltipTitle = !notifications.length
    ? 'No unread notification'
    : notifications.length > 1
      ? `${notifications.length} unread notifications`
      : 'One unread notification';

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton onClick={handleOpenNotifications}>
          {!notifications.length ? (
            <NotificationsNoneIcon />
          ) : (
            <Badge badgeContent={notifications.length} color="primary">
              <NotificationsIcon />
            </Badge>
          )}
        </IconButton>
      </Tooltip>

      <Popover
        sx={{ mt: '45px' }}
        id="appbar-notifications"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: '450px',
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <List subheader={<ListSubheader>Notifications</ListSubheader>}>
          {notifications.map((notification) => (
            <ListItem key={notification.id} sx={{ pt: 0 }}>
              <ListItemIcon>
                {notification.type === NotificationType.NEW_REPORT ? (
                  <SummarizeIcon fontSize="large" color="primary" />
                ) : (
                  <TipsAndUpdatesIcon
                    fontSize="large"
                    sx={{ color: yellow[600] }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={notification.label}
                secondary={dayjs(notification.datetime).fromNow()}
              ></ListItemText>
              {notification.type === NotificationType.NEW_REPORT ? (
                <Tooltip title="Download report">
                  <IconButton
                    onClick={() => handleNewReportDownloadClick(notification)}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
}

export { Notifications };
